import { TimeDuration } from '@/shared/libs/time.lib';
import { generateUrlSearchParams } from '../utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PropertyDto } from '../types/property.type';
import { apiUri } from '@/constants/Constants';
import { client } from '@/utils/axios/axiosClient';
import { useDebounce } from '@/utils/useDebounce';
import {
  MappedPropertyList,
  mapPropertyListResultToPropertyList,
} from '@/features/RentersInsurance/utils/property-list-mappers.util';

interface Pagination {
  limit: number;
  page: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
}

export interface ListPropertiesResponse {
  result: PropertyDto[];
  pagination: Pagination;
}

export interface ListPropertiesResult {
  result: MappedPropertyList;
  pagination: Pagination;
}

export interface ListPropertiesUrlParams {
  search: string;
  limit: number;
  page: number;
}

export async function listProperties(
  params: ListPropertiesUrlParams,
): Promise<ListPropertiesResponse> {
  const urlParams = generateUrlSearchParams(params);

  const response = await client.get<ListPropertiesResponse>(
    `${apiUri.getPropertyList}?${urlParams}`,
    {
      withCredentials: true,
    },
  );

  return response.data;
}

const FALLBACK_CACHE_KEY = 'any';

export const usePropertyList = (
  params: ListPropertiesUrlParams,
  { enabled = false }: { enabled: boolean },
) => {
  const { limit, page, search } = params;

  const debouncedName = useDebounce(search ?? FALLBACK_CACHE_KEY, 500);
  const cacheKey = ['propertyList', page, limit, debouncedName];

  const data = useQuery({
    queryKey: cacheKey,
    queryFn: async (): Promise<ListPropertiesResult> => {
      const response = await listProperties(params);
      const result = mapPropertyListResultToPropertyList(response.result);

      return {
        ...response,
        result,
      };
    },
    retry: 0,
    enabled,
    placeholderData: keepPreviousData,
    staleTime: TimeDuration.Minute * 2,
    refetchOnWindowFocus: false,
  });

  return data;
};
